const jsonStringifyReplacer = (_key: string, value: unknown) =>
  typeof value === 'bigint' ? value.toString() : value;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const sortJsonByReference = (value: unknown, reference: unknown): unknown => {
  // Early return for primitives
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    const referenceArray = Array.isArray(reference) ? reference : [];
    return value.map((item, index) =>
      sortJsonByReference(item, referenceArray[index])
    );
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const referenceObject = isPlainObject(reference) ? reference : undefined;
  const orderedObject: Record<string, unknown> = {};

  if (referenceObject) {
    // First, add keys that exist in both objects (preserving reference order)
    for (const key of Object.keys(referenceObject)) {
      if (key in value) {
        orderedObject[key] = sortJsonByReference(
          value[key],
          referenceObject[key]
        );
      }
    }

    // Then, add extra keys from value (sorted alphabetically)
    const extraKeys = Object.keys(value)
      .filter(key => !(key in referenceObject))
      .sort((left, right) => left.localeCompare(right));

    for (const key of extraKeys) {
      orderedObject[key] = sortJsonByReference(value[key], undefined);
    }
  } else {
    // No reference: sort all keys alphabetically
    const sortedKeys = Object.keys(value).sort((left, right) =>
      left.localeCompare(right)
    );

    for (const key of sortedKeys) {
      orderedObject[key] = sortJsonByReference(value[key], undefined);
    }
  }

  return orderedObject;
};

export const formatJson = (value: unknown, reference?: unknown) => {
  if (value === undefined) {
    return 'undefined';
  }

  const orderedValue =
    reference === undefined ? value : sortJsonByReference(value, reference);

  try {
    const formatted = JSON.stringify(orderedValue, jsonStringifyReplacer, 4);

    if (!formatted) {
      return String(value);
    }

    // Add trailing commas after values that are followed by a newline and closing bracket/brace
    // This regex matches: value (string, number, boolean, null, ], }) followed by newline and whitespace before ] or }
    const withTrailingCommas = formatted.replace(
      /("(?:[^"\\]|\\.)*"|true|false|null|-?\d+(?:\.\d+)?(?:[Ee][+-]?\d+)?|\]|\})(\n\s*)([}\]])/g,
      '$1,$2$3'
    );

    return withTrailingCommas;
  } catch {
    return String(value);
  }
};
